import { Modal, Button } from '@bfe/moly';
import Clock from '@/assets/svg/home/clock.svg';
import IconClock from '@/assets/svg/home/icon-clock.svg';
import IconPending from '@/assets/svg/icon-pending.svg';
import IconSuccess from '@/assets/svg/icon-success.svg';
import clsx from 'clsx';
import { CheckinHistoryItem } from '@/types';
import useHomeStore from '@/store/home';
import { useEffect, useMemo, useState } from 'react';
import { getWalletAsset, postCheckin } from '@/services/home';
import { useWallet } from '@/hooks/wallet';
import { useFm, useSetParams, useToast } from '@web3/base-hooks';
import { NoSSR } from '@web3/base-components';
import { ConnectX, CallbackParam } from './ConnetX';
import { isBrowser, origin } from '@web3/base-utils';
import { Event, Page } from '@/config/track-config';
import { scTrack } from '@web3/base-tracking';
import { CheckAssetsListButton } from '@/components/CheckAssetsListButton';
// import { useRouter } from 'next/router';

type IDailyCheckIn = {
  visble: boolean;
  data?: CheckinHistoryItem[];
  loading?: boolean;
  onOk?: (callbackParam?: CallbackParam) => void;
  onCancel?: () => void;
};
export const DailyCheckIn = (props: IDailyCheckIn) => {
  const { visble, data, loading, onOk, onCancel } = props;
  const [showConnectX, setShowConnectX] = useState(false);
  const [isCapital, setIsCapital] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { walletId, walletType, twitterConnectCodeKey, supportCommonTask } =
    useWallet();
  const { fetchWalletStat, fetchWalletTotalPoints } = useHomeStore();
  const { toastSuccess, toastFail } = useToast();
  const fm = useFm();
  const { setSearchParams } = useSetParams();

  const { walletConfig } = useHomeStore();

  const paramsBase = useMemo(
    () => ({
      walletId,
      walletType,
    }),
    [walletId, walletType]
  );

  const checkedData = useMemo(() => {
    const hasCheckedData = data.filter((item) => item.hasCheckin);
    const todayData = data.filter((item) => item.today);
    return {
      hasCheckin: hasCheckedData.length,
      noCheckin: data.length - hasCheckedData.length,
      todayCheckin: todayData[0]?.hasCheckin,
    };
  }, [data]);

  const buttonText = useMemo(() => {
    let text = fm('signToday');
    if (checkedData.todayCheckin) {
      text = fm('signedInToday');
    }
    if (isCapital) {
      text = fm('insufficientBalance');
    }
    return text;
  }, [checkedData, isCapital]);

  const checkCapital = async () => {
    setIsLoading(true);
    const capitalRes = await getWalletAsset({
      walletId,
      walletType,
    });
    if (!capitalRes.success) {
      setIsLoading(false);
      toastFail(capitalRes.message);
      return;
    }
    // 资沉不足
    if (!capitalRes.result?.capital) {
      setIsCapital(true);
      // toastFail(
      //   fm('walletAssetLessTenU', {
      //     capital: walletConfig.capital,
      //   })
      // );
      setIsLoading(false);
      return;
    }
  };

  const share = () => {
    const shareText = encodeURIComponent(
      `This is my ${checkedData.hasCheckin + 1} day participating in the Bybit Web3 Points Check-In Challenge! Earning Web3 Points every day and aiming for big rewards! 🏆 #BybitWeb3 #Web3Points`
    );
    window.open(`https://x.com/intent/post?text=${shareText}`);
  };

  // 提交签到
  const doCheckin = async ({ code, type, searchParams }: CallbackParam) => {
    if (type && type !== 'checkin') return;
    if (code && type === 'checkin') {
      localStorage.setItem(twitterConnectCodeKey, code);
      share();
    }
    try {
      const res = await postCheckin({
        ...paramsBase,
        accessToken: code || 'accessToken',
        refreshToken: code || 'refreshToken',
      });
      if (res.success) {
        onOk();

        fetchWalletStat();
        fetchWalletTotalPoints(paramsBase);
        toastSuccess('Checkin success');
      } else {
        toastFail('Checkin fail');
      }
    } catch (error) {
      console.log(error);
      toastFail(error);
    }

    setIsLoading(false);
    if (code && isBrowser) {
      searchParams.delete('code');
      searchParams.delete('state');
      searchParams.delete('type');
      searchParams.delete('locale');
      setSearchParams(searchParams);
    }
  };

  const handleClick = async () => {
    setIsLoading(true);
    // 资沉达标
    const storageCode = localStorage.getItem(twitterConnectCodeKey);
    if (storageCode) {
      share();
      doCheckin({});
    } else {
      setShowConnectX(true);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (visble) {
      scTrack(Event.Web3PageView, {
        page_name: Page.CheckinPopUp,
      });
    }
    if (visble && !checkedData.todayCheckin) {
      checkCapital();
    }

    setIsLoading(false);
    setIsCapital(false);
  }, [walletId, checkedData, visble]);

  const handleTwitterAuthSuccess = async (code: string) => {
    if (!supportCommonTask) return;

    // 存储认证码
    localStorage.setItem(twitterConnectCodeKey, code);

    // 关闭弹窗
    setShowConnectX(false);

    // 显示成功提示
    toastSuccess(fm('connectSuccessfully'));

    // 可以选择在这里打开 Twitter 关注页面
    window.open('https://x.com/Bybit_Web3', '_blank');
  };

  return (
    <>
      <Modal
        title={
          <div className="flex items-center gap-3">
            <Clock viewBox="0 0 40 40" />
            <div>
              <h2 className="text-lg font-semibold">{fm('dailyCheckIn')}</h2>
              <p className="text-xs text-gray-t2">
                {fm('minWalletAssetTenU', {
                  capital: walletConfig.capital,
                })}
              </p>
              <CheckAssetsListButton />
            </div>
          </div>
        }
        footer={null}
        open={visble}
        onCancel={onCancel}
      >
        <div className="flex flex-wrap gap-y-4 gap-x-2 justify-center">
          {data.map((item, index) => (
            <div
              className={clsx(
                'w-[23%] bg-edge rounded-md overflow-hidden',
                item.today && 'border-[1px] border-[#9747FF]'
              )}
              key={index}
            >
              <div
                className={clsx(
                  'w-full h-[26px] py-1 flex justify-center items-center gap-1 text-xs font-semibold',
                  item.today ? 'bg-brand' : 'bg-border'
                )}
              >
                <IconClock />
                {item.today ? 'Today' : `Day ${index + 1}`}
              </div>
              <div className="flex flex-col items-center justify-between gap-1 px-2 pt-3 pb-2">
                {item.hasCheckin ? <IconSuccess /> : <IconPending />}
                <div className="flex items-center gap-1">
                  <span
                    className={clsx(
                      'text-lg font-semibold flex items-center',
                      item.today && 'text-main'
                    )}
                  >
                    <span className="transform -translate-y-[1px]">+</span>
                    <span>{item.point}</span>
                  </span>
                  <span className="text-xs text-gray-t3">Points</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div
          className="bg-edge p-2 mt-4 text-sm text-center rounded"
          // dangerouslySetInnerHTML={{
          // __html: fm('continuousSignInfo', {
          //   num1: checkedData.hasCheckin,
          //   num2: checkedData.noCheckin,
          //   num3: (
          //     <span className="text-main font-semibold">
          //       {data[data.length - 1]?.point}
          //     </span>
          //   ),
          // }),
          // .replace(/&lt;/g, '<')
          // .replace(/&gt;/g, '>'),
          // }}
        >
          {fm('continuousSignInfo', {
            num1: checkedData.hasCheckin,
            num2: checkedData.noCheckin,
            num3: (
              <span className="text-main font-semibold">
                +{data[data.length - 1]?.point}
              </span>
            ),
          })}
        </div>
        <Button
          variant={'primary'}
          className="mt-6 w-full"
          onClick={handleClick}
          loading={loading || isLoading}
          disabled={checkedData.todayCheckin || isCapital}
          data-spm-even="Web3UserClick"
          data-spm-data={JSON.stringify({
            page_name: Page.CheckinPopUp,
            button_name: 'Web3Points_CheckIn',
          })}
        >
          {buttonText}
        </Button>
      </Modal>
      <NoSSR>
        <ConnectX
          show={showConnectX}
          onConnect={handleTwitterAuthSuccess}
          onCancel={() => setShowConnectX(false)}
        />
      </NoSSR>
    </>
  );
};
